import React, { useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Authenticator, useAuthenticator, ThemeProvider as AmplifyThemeProvider } from '@aws-amplify/ui-react';

import '@aws-amplify/ui-react/styles.css';
import './authenticator.css';
import { Amplify } from 'aws-amplify';
import awsconfig from './aws-exports';

// State for error handling
import { Alert } from '@mui/material';

// Material UI Components
import {
  Typography,
  Button,
  IconButton,
  Box
} from '@mui/material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';

// Custom Hooks
import { useFileOperations } from './hooks/useFileOperations';
import { useJobParameters } from './hooks/useJobParameters';
import { callApi } from './utils/apiHelper';
import NavigationBar from './components/NavigationBar';
import HomePage from './components/HomePage';
import DashboardPage from './components/DashboardPage';
import JobCreationPage from './components/JobCreationPage';
import UserProfile from './components/UserProfile';
import { BrowserRouter, Route, Routes, useNavigate, Navigate } from 'react-router-dom';

Amplify.configure(awsconfig);

export default function App() {
  const [mode, setMode] = useState('light');

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const dynamicTheme = createTheme({
    palette: {
      mode,
      primary: {
        main: '#1976d2',
      },
      secondary: {
        main: '#dc004e',
      },
    },
  });

  // Custom theme for Authenticator component
  const amplifyTheme = {
    name: 'custom-theme',
    tokens: {
      colors: {
        background: {
          primary: mode === 'dark' ? '#121212' : '#ffffff',
          secondary: mode === 'dark' ? '#1e1e1e' : '#f5f5f5',
        },
        font: {
          interactive: '#1976d2',
        },
        brand: {
          primary: {
            10: '#e3f2fd',
            20: '#bbdefb',
            40: '#64b5f6',
            60: '#1976d2',
            80: '#1565c0',
            90: '#0d47a1',
            100: '#0a3d91',
          },
        },
      },
      components: {
        authenticator: {
          router: {
            boxShadow: mode === 'dark' ? '0 0 16px rgba(255, 255, 255, 0.1)' : '0 0 16px rgba(0, 0, 0, 0.15)',
            borderWidth: '0',
          },
        },
        button: {
          primary: {
            backgroundColor: '#1976d2',
            _hover: {
              backgroundColor: '#1565c0',
            },
            _focus: {
              backgroundColor: '#1565c0',
            },
            _active: {
              backgroundColor: '#0d47a1',
            },
          },
        },
        fieldcontrol: {
          _focus: {
            borderColor: '#1976d2',
          },
        },
        tabs: {
          item: {
            color: mode === 'dark' ? '#ffffff' : '#000000',
            _active: {
              borderColor: '#1976d2',
              color: '#1976d2',
            },
            _hover: {
              color: '#1976d2',
            },
          },
        },
      },
    },
  };

  return (
    <ThemeProvider theme={dynamicTheme}>
      <CssBaseline />
      <Authenticator.Provider>
        <AppContent toggleTheme={toggleTheme} mode={mode} amplifyTheme={amplifyTheme} />
      </Authenticator.Provider>
    </ThemeProvider>
  );
}

function AppContent({ toggleTheme, mode, amplifyTheme }) {
  const { user, signOut, authStatus } = useAuthenticator((context) => [
    context.user,
    context.signOut,
    context.authStatus
  ]);
  const [error, setError] = useState(null);

  const fileOperations = useFileOperations(
    user,
    (params) => callApi({ user, ...params }),
    setError
  );

  const jobParameters = useJobParameters();

  if (authStatus === 'configuring') return <div>Loading...</div>;
  if (authStatus !== 'authenticated') {
    return (
      <Box
        className={mode === 'dark' ? 'authenticator-dark' : 'authenticator-light'}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: mode === 'dark' ? '#121212' : '#f5f5f5',
          position: 'relative',
        }}
      >
        {/* Theme toggle button */}
        <IconButton
          onClick={toggleTheme}
          sx={{
            position: 'absolute',
            top: '1.5em',
            right: '1.5em',
            color: mode === 'dark' ? '#ffffff' : '#424242',
            backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
            '&:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
            },
          }}
        >
          {mode === 'light' ? <Brightness4Icon /> : <Brightness7Icon />}
        </IconButton>

        {/* Logo */}
        <Box
          component="a"
          href="https://edansoul.com"
          sx={{
            width: '150px',
            height: '150px',
            borderRadius: '50%',
            backgroundColor: mode === 'dark' ? '#1e1e1e' : '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: mode === 'dark'
              ? '0 4px 24px rgba(0, 0, 0, 0.5)'
              : '0 4px 24px rgba(0, 0, 0, 0.12)',
            marginBottom: '2em',
            cursor: 'pointer',
            textDecoration: 'none',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'scale(1.05)',
              boxShadow: mode === 'dark'
                ? '0 6px 28px rgba(0, 0, 0, 0.6)'
                : '0 6px 28px rgba(0, 0, 0, 0.18)',
            },
          }}>
          <img
            src={`${process.env.PUBLIC_URL}/edansoul.png`}
            alt="Edansoul Logo"
            style={{
              width: '100px',
              height: '100px',
              objectFit: 'contain',
            }}
          />
        </Box>

        {/* Authenticator */}
        <AmplifyThemeProvider theme={amplifyTheme}>
          <Authenticator />
        </AmplifyThemeProvider>
      </Box>
    );
  }



  return (
    <Box sx={{
      width: '80%',           // Changed to 80% of viewport width
      minWidth: '1200px',      // Added minimum width for responsiveness
      maxWidth: '3200px',     // Added maximum width to prevent extreme stretching
      margin: '0 auto',       // Center the content
      padding: '0 1em'        // Optional: add some padding
    }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1em'
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5em'
        }}>
          <img
            // src={`${process.env.PUBLIC_URL}/edansoul.png`}
            src="/edansoul.png"
            alt="Logo"
            style={{ width: '28px', height: '28px', verticalAlign: 'middle' }}
          />
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              lineHeight: '1',
              margin: '0'
            }}
          >
            Welcome to the Edansoul TS Service!
          </Typography>
        </Box>
        <IconButton onClick={toggleTheme} color="inherit">
          {mode === 'light' ? <Brightness4Icon /> : <Brightness7Icon />}
        </IconButton>
      </Box>

      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1em'
      }}>
        <UserProfile mode={mode} />
        <Button
          variant="contained"
          color="primary"
          onClick={signOut}
        >
          Sign out
        </Button>
      </Box>

      <BrowserRouter>
        <NavigationBar mode={mode} />
        <Routes>
          {/* Default route to /dashboard */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/file_manager" element={<HomePage
            mode={mode}
            fileOperations={fileOperations}
            jobParameters={jobParameters}
            user={user}
            callApi={callApi}
          />} />
          <Route path="/dashboard" element={<DashboardPage
            mode={mode}
          />} />
          <Route path="/job-manager" element={<JobCreationPageWrapper
            mode={mode}
          />} />
        </Routes>
      </BrowserRouter>

      {error && (
        <Alert severity="error" sx={{ marginTop: '1em' }}>
          {error.message || JSON.stringify(error)}
        </Alert>
      )}
    </Box>
  );
}

/**
 * Wrapper component for JobCreationPage to handle navigation
 */
function JobCreationPageWrapper({ mode }) {
  const navigate = useNavigate();

  const handleNavigateBack = () => {
    navigate('/dashboard');
  };

  return (
    <JobCreationPage
      mode={mode}
      onNavigateBack={handleNavigateBack}
    />
  );
}
